import { BUILDING_DEFS } from './constants';
import type { Simulation } from './sim';
import type { EndingKind, LegacyBreakdown } from './types';

const TITLES: Record<EndingKind, string> = {
  endures: 'The Town That Endured',
  strip: 'The Strip Job',
  ghost: 'Dust and Silence',
  foreclosed: 'Foreclosed',
};

/** Per-ending category weights (each category is scored 0..1000). */
const WEIGHTS: Record<EndingKind, [number, number, number, number, number]> = {
  //           fortune people selfSuff quality record
  endures: [0.15, 0.3, 0.3, 0.15, 0.1],
  strip: [0.6, 0.1, 0.0, 0.1, 0.2],
  ghost: [0.3, 0.2, 0.1, 0.1, 0.3],
  foreclosed: [0.4, 0.2, 0.0, 0.1, 0.3],
};

const ENDING_MULTIPLIER: Record<EndingKind, number> = {
  endures: 1,
  strip: 1,
  ghost: 0.5,
  foreclosed: 0.3,
};

function cap(value: number, maximum = 1000): number {
  return Math.max(0, Math.min(maximum, Math.round(value)));
}

export function computeLegacy(sim: Simulation, kind: EndingKind): LegacyBreakdown {
  const state = sim.state;
  const town = state.town;

  const fortune = cap(state.economy.cashCents / 100 / 20); // $20,000 = full marks

  const people = cap(
    state.peeps.length * 8 + state.households.length * 30 + sim.kidsGrownUp * 60,
  );

  const mineAvg = town.mineIncomeHistory.reduce((a, b) => a + b, 0) / Math.max(1, town.mineIncomeHistory.length);
  const townAvg = town.townIncomeHistory.reduce((a, b) => a + b, 0) / Math.max(1, town.townIncomeHistory.length);
  const townShare = townAvg / Math.max(1, mineAvg + townAvg);
  const selfSufficiency = cap(townShare * 500 + town.endureStreak * 45);

  let amenityPoints = 0;
  let civicCount = 0;
  for (const building of state.buildings) {
    if (!building.functional) continue;
    amenityPoints += (BUILDING_DEFS[building.type].attract ?? 0) * 700;
    if (BUILDING_DEFS[building.type].service || building.type === 'townHall') civicCount += 1;
  }
  const avgHappiness =
    state.households.length > 0
      ? state.households.reduce((sum, h) => sum + h.happiness, 0) / state.households.length
      : 0;
  const townQuality = cap(amenityPoints + civicCount * 40 + avgHappiness * 250 + state.workforce.familyNeeds.air * 100);

  const companyRecord = cap(
    town.quotasCompleted * 200 +
      (state.economy.bailoutsUsed === 0 ? 250 : 0) +
      town.oreMinedTotalDt / 60,
  );

  const weights = WEIGHTS[kind];
  const total = Math.round(
    (fortune * weights[0] +
      people * weights[1] +
      selfSufficiency * weights[2] +
      townQuality * weights[3] +
      companyRecord * weights[4]) *
      10 *
      ENDING_MULTIPLIER[kind],
  );

  return {
    fortune,
    people,
    selfSufficiency,
    townQuality,
    companyRecord,
    total,
    title: TITLES[kind],
  };
}
